import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import FoodPage from '../components/knowledge/FoodPage';
import { GuidePage, RecipePage, MealPlanPage, HubPage } from '../components/knowledge/GuidePage';
import {
  foods,
  guides,
  mealPlans,
  recipes,
  getFoodBySlug,
  getGuideBySlug,
  getMealPlanBySlug,
  getRecipeBySlug,
  localizeItem,
  recipesForLang,
} from '../content';
import { useContentLang, useLocalizedPath } from '../hooks/useLocalizedContent';

function truncate(text, length = 140) {
  if (!text) return '';
  return text.length > length ? `${text.slice(0, length).trim()}…` : text;
}

export function FoodsHub() {
  const { t } = useTranslation();
  const lang = useContentLang();
  return (
    <HubPage
      title={t('knowledge.page.hubs.foodsTitle')}
      kicker={t('knowledge.page.hubKicker')}
      path="/foods"
      description={t('knowledge.page.hubs.foodsDescription')}
      itemLabel={t('knowledge.page.hubs.foodsLabel')}
      items={foods.map((food) => {
        const item = localizeItem(food, lang);
        return {
          name: item.name,
          path: item.path,
          imageUrl: item.imageUrl,
          shortAnswer: truncate(item.shortAnswer),
        };
      })}
    />
  );
}

export function GuidesHub() {
  const { t } = useTranslation();
  const lang = useContentLang();
  return (
    <HubPage
      title={t('knowledge.page.hubs.guidesTitle')}
      kicker={t('knowledge.page.hubKicker')}
      path="/guides"
      description={t('knowledge.page.hubs.guidesDescription')}
      itemLabel={t('knowledge.page.hubs.guidesLabel')}
      items={guides.map((guide) => {
        const item = localizeItem(guide, lang);
        return {
          title: item.title,
          path: item.path,
          shortAnswer: truncate(item.shortAnswer),
        };
      })}
    />
  );
}

export function MealPlansHub() {
  const { t } = useTranslation();
  const lang = useContentLang();
  return (
    <HubPage
      title={t('knowledge.page.hubs.plansTitle')}
      kicker={t('knowledge.page.hubKicker')}
      path="/meal-plans"
      description={t('knowledge.page.hubs.plansDescription')}
      itemLabel={t('knowledge.page.hubs.plansLabel')}
      items={mealPlans.map((plan) => {
        const item = localizeItem(plan, lang);
        return {
          title: item.title,
          path: item.path,
          shortAnswer: truncate(item.shortAnswer),
        };
      })}
    />
  );
}

export function RecipesHub() {
  const { t } = useTranslation();
  const lang = useContentLang();
  return (
    <HubPage
      title={t('knowledge.page.hubs.recipesTitle')}
      kicker={t('knowledge.page.hubKicker')}
      path="/recipes"
      description={t('knowledge.page.hubs.recipesDescription')}
      itemLabel={t('knowledge.page.hubs.recipesLabel')}
      items={recipesForLang(recipes, lang).map((recipe) => {
        const item = localizeItem(recipe, lang);
        return {
          title: item.title,
          path: item.path,
          imageUrl: item.imageUrl,
          shortAnswer: truncate(item.shortAnswer),
        };
      })}
    />
  );
}

export function FoodRoute() {
  const { slug } = useParams();
  const toPath = useLocalizedPath();
  const food = getFoodBySlug(slug);
  if (!food) return <Navigate to={toPath('/foods')} replace />;
  return <FoodPage food={food} />;
}

export function GuideRoute() {
  const { slug } = useParams();
  const toPath = useLocalizedPath();
  const guide = getGuideBySlug(slug);
  if (!guide) return <Navigate to={toPath('/guides')} replace />;
  return <GuidePage guide={guide} />;
}

export function MealPlanRoute() {
  const { slug } = useParams();
  const toPath = useLocalizedPath();
  const plan = getMealPlanBySlug(slug);
  if (!plan) return <Navigate to={toPath('/meal-plans')} replace />;
  return <MealPlanPage plan={plan} />;
}

export function RecipeRoute() {
  const { slug } = useParams();
  const toPath = useLocalizedPath();
  const recipe = getRecipeBySlug(slug);
  if (!recipe) return <Navigate to={toPath('/recipes')} replace />;
  return <RecipePage recipe={recipe} />;
}
